import { parseDateOnly } from './helpers';

export const getSalesGrowth = (curr, prev) => {
    if (!prev || prev === 0) return curr > 0 ? 100 : 0;
    return ((curr - prev) / prev) * 100;
};

export const salesDateKey = (value) => (value ? String(value).slice(0, 10) : '');

const DISCOUNT_AMOUNT_FIELDS = [
    'discount_amount',
    'discountAmount',
    'discount_total',
    'discountTotal',
    'discount_value',
    'discountValue',
    'coupon_amount',
    'couponAmount',
    'voucher_amount',
    'voucherAmount',
    'rabatt_betrag',
    'rabattBetrag',
    'rabattwert',
    'Rabattwert',
    'RabattBetrag',
    'fRabattwertNetto',
    'fRabattwertBrutto'
];

const DISCOUNT_RATE_FIELDS = [
    'discount_rate',
    'discountRate',
    'discount_percent',
    'discountPercent',
    'rabatt_rate',
    'rabattRate',
    'rabatt_quote',
    'rabattQuote',
    'rabatt_percent',
    'rabattPercent',
    'fRabatt'
];

const GROSS_BEFORE_DISCOUNT_FIELDS = [
    'gross_revenue_before_discount',
    'grossRevenueBeforeDiscount',
    'revenue_before_discount',
    'revenueBeforeDiscount'
];

const toFiniteNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const pickFirstFiniteNumber = (row, fields) => {
    for (const field of fields) {
        const value = toFiniteNumber(row?.[field]);
        if (value !== null) return value;
    }
    return null;
};

export const calculateDiscountRateFromFacts = (facts = []) => {
    let discountAmount = 0;
    let grossBeforeDiscount = 0;
    let revenueWithDiscountSource = 0;
    let weightedRateSum = 0;
    let rateWeight = 0;

    (facts || []).forEach(row => {
        const revenue = toFiniteNumber(row?.revenue)
            ?? toFiniteNumber(row?.netRevenue)
            ?? toFiniteNumber(row?.net_revenue)
            ?? 0;
        const amount = pickFirstFiniteNumber(row, DISCOUNT_AMOUNT_FIELDS);

        if (amount !== null) {
            discountAmount += Math.abs(amount);
            const gross = pickFirstFiniteNumber(row, GROSS_BEFORE_DISCOUNT_FIELDS);
            if (gross !== null && gross > 0) {
                grossBeforeDiscount += gross;
            } else {
                revenueWithDiscountSource += Math.max(revenue, 0);
            }
            return;
        }

        const rate = pickFirstFiniteNumber(row, DISCOUNT_RATE_FIELDS);
        if (rate !== null) {
            const weight = Math.max(revenue, 1);
            weightedRateSum += Math.max(0, Math.min(rate, 100)) * weight;
            rateWeight += weight;
        }
    });

    if (discountAmount > 0 || grossBeforeDiscount > 0 || revenueWithDiscountSource > 0) {
        const grossBase = grossBeforeDiscount > 0 ? grossBeforeDiscount : revenueWithDiscountSource + discountAmount;
        return grossBase > 0 ? (discountAmount / grossBase) * 100 : null;
    }

    return rateWeight > 0 ? weightedRateSum / rateWeight : null;
};

const getISOWeek = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return { year: d.getUTCFullYear(), week };
};

const getBucketFromOffset = (startDate, index, granularity) => {
    const d = new Date(startDate);
    d.setHours(0, 0, 0, 0);

    if (granularity === 'day') {
        d.setDate(d.getDate() + index);
        return {
            key: String(index).padStart(5, '0'),
            label: d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
        };
    }

    if (granularity === 'week') {
        d.setDate(d.getDate() + index * 7);
        const iso = getISOWeek(d);
        return {
            key: String(index).padStart(5, '0'),
            label: `KW ${String(iso.week).padStart(2, '0')}`
        };
    }

    if (granularity === 'year') {
        d.setFullYear(d.getFullYear() + index);
        return {
            key: String(index).padStart(5, '0'),
            label: String(d.getFullYear())
        };
    }

    d.setMonth(d.getMonth() + index);
    return {
        key: String(index).padStart(5, '0'),
        label: d.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' })
    };
};

const getBucketIndex = (date, startDate, granularity) => {
    const d = new Date(date);
    const s = new Date(startDate);
    d.setHours(0, 0, 0, 0);
    s.setHours(0, 0, 0, 0);

    if (granularity === 'day') return Math.floor((d - s) / 86400000);
    if (granularity === 'week') return Math.floor((d - s) / (86400000 * 7));
    if (granularity === 'year') return d.getFullYear() - s.getFullYear();
    return (d.getFullYear() - s.getFullYear()) * 12 + (d.getMonth() - s.getMonth());
};

const getPlatformGroup = (name) => {
    const lower = String(name || '').toLowerCase();
    if (lower.includes('amazon')) return 'Amazon';
    if (lower.includes('ebay')) return 'eBay';
    if (lower.includes('shop') || lower.includes('jtl')) return 'Shop';
    return name || 'Other';
};

export const buildSalesAnalysisSnapshot = ({
    rawFacts = [],
    prevRawFacts = [],
    globalStartDate,
    globalEndDate,
    filters = {}
}) => {
    const {
        platform = 'all',
        customerGroup = 'all',
        selectedCountry = 'all',
        selectedArticle = null
    } = filters;

    const start = globalStartDate ? parseDateOnly(globalStartDate) : new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const end = globalEndDate ? parseDateOnly(globalEndDate) : new Date();
    const startKey = salesDateKey(globalStartDate);
    const endKey = salesDateKey(globalEndDate);

    if (!start || !end) {
        return {
            processedData: {
                currentKPIs: { netRevenue: 0, grossMargin: 0, marginPct: 0, aov: 0, discountRate: null, orders: 0 },
                prevKPIs: { netRevenue: 0, grossMargin: 0, marginPct: 0, aov: 0, discountRate: null, orders: 0 },
                growth: { rev: 0, margin: 0, aov: 0, discount: null },
                currentFacts: [],
                prevFacts: [],
                channelData: [],
                countries: [],
                start: null,
                end: null,
                prevStart: null
            },
            timelineByGranularity: {}
        };
    }

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const currentRangeMs = end - start;
    const prevEnd = new Date(start.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - currentRangeMs);
    prevStart.setHours(0, 0, 0, 0);
    prevEnd.setHours(23, 59, 59, 999);

    const getFiltered = (sourceRows, rangeStartKey = '', rangeEndKey = '') => {
        return sourceRows.filter(row => {
            if (rangeStartKey && rangeEndKey) {
                const orderDate = salesDateKey(row.order_date);
                if (!orderDate) return false;
                if (orderDate < rangeStartKey || orderDate > rangeEndKey) return false;
            }

            if (platform !== 'all') {
                const currentPlatform = row.platform || 'Unbekannt';
                const lower = currentPlatform.toLowerCase();
                const isShop = lower.includes('shop') || lower.includes('jtl');
                const isMarketplace = lower.includes('amazon') || lower.includes('ebay');
                if (platform === 'shop' && !isShop) return false;
                if (platform === 'marketplaces' && !isMarketplace) return false;
                if (platform !== 'shop' && platform !== 'marketplaces' && currentPlatform !== platform) return false;
            }

            if (customerGroup !== 'all') {
                const rowCustomerGroup = String(row.customer_group || row.KundenGruppe || '').trim();
                if (rowCustomerGroup !== customerGroup) return false;
            }
            if (selectedCountry !== 'all' && row.country !== selectedCountry) return false;

            if (selectedArticle) {
                const hasArticle = (row.items || []).some(item =>
                    String(item.name || item.article_name || item.id || '').toLowerCase().includes(selectedArticle.toLowerCase())
                );
                if (!hasArticle && row.article_name !== selectedArticle) return false;
            }

            return true;
        });
    };

    const currentFacts = getFiltered(rawFacts, startKey, endKey);
    const previousSource = prevRawFacts && prevRawFacts.length ? prevRawFacts : rawFacts;
    const prevFacts = getFiltered(
        previousSource,
        prevRawFacts && prevRawFacts.length ? '' : salesDateKey(prevStart),
        prevRawFacts && prevRawFacts.length ? '' : salesDateKey(prevEnd)
    );

    const calcKPIs = (facts) => {
        const revenue = facts.reduce((sum, row) => sum + Number(row.revenue || 0), 0);
        const profit = facts.reduce((sum, row) => sum + Number(row.profit || 0), 0);
        const orders = facts.reduce((sum, row) => sum + Number(row.order_count || 0), 0);
        const discountRate = calculateDiscountRateFromFacts(facts);
        return {
            netRevenue: revenue,
            grossMargin: profit,
            marginPct: revenue > 0 ? (profit / revenue) * 100 : 0,
            aov: orders > 0 ? revenue / orders : 0,
            discountRate,
            orders
        };
    };

    const currentKPIs = calcKPIs(currentFacts);
    const prevKPIs = calcKPIs(prevFacts);
    const growth = {
        rev: getSalesGrowth(currentKPIs.netRevenue, prevKPIs.netRevenue),
        margin: getSalesGrowth(currentKPIs.grossMargin, prevKPIs.grossMargin),
        aov: getSalesGrowth(currentKPIs.aov, prevKPIs.aov),
        discount: currentKPIs.discountRate !== null && prevKPIs.discountRate !== null
            ? currentKPIs.discountRate - prevKPIs.discountRate
            : null
    };

    const channelMap = {};
    currentFacts.forEach(row => {
        const currentPlatform = row.platform || 'Unbekannt';
        if (!channelMap[currentPlatform]) channelMap[currentPlatform] = { name: currentPlatform, currRev: 0, prevRev: 0, margin: 0, orders: 0 };
        channelMap[currentPlatform].currRev += Number(row.revenue || 0);
        channelMap[currentPlatform].margin += Number(row.profit || 0);
        channelMap[currentPlatform].orders += Number(row.order_count || 0);
    });

    prevFacts.forEach(row => {
        const currentPlatform = row.platform || 'Unbekannt';
        if (!channelMap[currentPlatform]) channelMap[currentPlatform] = { name: currentPlatform, currRev: 0, prevRev: 0, margin: 0, orders: 0 };
        channelMap[currentPlatform].prevRev += Number(row.revenue || 0);
    });

    const channelData = Object.values(channelMap)
        .map(channel => ({
            ...channel,
            group: getPlatformGroup(channel.name),
            growth: getSalesGrowth(channel.currRev, channel.prevRev),
            marginPct: channel.currRev > 0 ? (channel.margin / channel.currRev) * 100 : 0,
            aov: channel.orders > 0 ? channel.currRev / channel.orders : 0
        }))
        .sort((a, b) => b.currRev - a.currRev);

    const granularities = ['day', 'week', 'month', 'year'];
    const maps = granularities.reduce((acc, key) => {
        acc[key] = {};
        return acc;
    }, {});

    const ensureBucket = (granularity, index) => {
        const bucket = getBucketFromOffset(start, index, granularity);
        if (!maps[granularity][bucket.key]) {
            maps[granularity][bucket.key] = {
                key: bucket.key,
                name: bucket.label,
                currRev: 0,
                currMargin: 0,
                prevRev: 0,
                diffPct: 0
            };
        }
        return maps[granularity][bucket.key];
    };

    const addRows = (rows, periodStart, assign) => {
        rows.forEach(row => {
            const date = parseDateOnly(row.order_date);
            if (!date) return;
            granularities.forEach(granularity => {
                const index = getBucketIndex(date, periodStart, granularity);
                if (index < 0) return;
                assign(ensureBucket(granularity, index), row);
            });
        });
    };

    addRows(currentFacts, start, (bucket, row) => {
        bucket.currRev += Number(row.revenue || 0);
        bucket.currMargin += Number(row.profit || 0);
    });

    addRows(prevFacts, prevStart, (bucket, row) => {
        bucket.prevRev += Number(row.revenue || 0);
    });

    const timelineByGranularity = granularities.reduce((acc, granularity) => {
        const indexes = Object.keys(maps[granularity]).map(key => Number(key)).filter(Number.isFinite);
        const maxIndex = indexes.length ? Math.max(...indexes) : 0;
        for (let i = 0; i <= maxIndex; i += 1) ensureBucket(granularity, i);

        acc[granularity] = Object.values(maps[granularity])
            .map(row => ({
                ...row,
                diffPct: row.prevRev > 0
                    ? ((row.currRev - row.prevRev) / row.prevRev) * 100
                    : (row.currRev > 0 ? 100 : 0)
            }))
            .sort((a, b) => a.key.localeCompare(b.key));
        return acc;
    }, {});

    return {
        processedData: {
            currentKPIs,
            prevKPIs,
            growth,
            currentFacts,
            prevFacts,
            channelData,
            countries: Array.from(new Set(rawFacts.map(row => row.country).filter(Boolean))).sort(),
            start,
            end,
            prevStart
        },
        timelineByGranularity
    };
};
